import { FC, useEffect, useMemo } from "react";

import {
  Box,
  Column,
  NumberInput,
  Row,
  Select,
  Text,
  Tooltip,
  WarningIcon,
} from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { HiddenFormLabel } from "src/components/form/hidden-form-label";
import { singular } from "pluralize";
import { isPresent } from "ts-extras";
import { v4 as uuidv4 } from "uuid";

import { MinimalParentModel } from "src/components/audiences/types";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { FilterPopover } from "src/components/explore/filter-popover";
import {
  createSubconditionUpdater,
  groupSubcondition,
  OnUpdate,
  removeSubconditionMutative,
  ungroupSubcondition,
} from "src/components/explore/utils/condition-builders";
import { formatSubconditions } from "src/components/explore/utils/format-subconditions";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { useFormErrorContext } from "src/contexts/form-error-context";
import { RelationshipWithoutFilterableColumnsFragment } from "src/graphql";
import {
  Audience,
  Condition,
  ConditionType,
  FunnelCondition,
  InlineTraitCondition,
  ModelSemanticType,
  NestableCondition,
  NumberOfCondition,
  NumberOperator,
  PropertyComparisonType,
  PropertyCondition,
  TraitCondition,
  isTraitCondition,
  numberOfOperatorOptions,
} from "src/types/visual";

import { ConditionField, FilterProps } from "./condition";
import { WhereButton } from "./condition-buttons";
import { validateNumberOfCondition } from "./condition-validation";
import { ErrorMessage } from "./error-message";
import { Filter } from "./filter";
import { NestedPropertyFilter } from "./nested-property-filter";
import { NestedTraitFilter } from "./nested-trait-filter";
import { HStack } from "./shared";
import { FilterableOptionsProvider } from "src/components/explore/filter-popover/filterable-options-context";
import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import {
  FilterOption,
  getRelationSubconditionFilterableSections,
} from "src/components/explore/filter-popover/constants";
import { UpdaterAdvanced } from "src/utils/immutable-helpers";
import { NestedRelationConditionWrapper } from "./condition-wrapper";
import { getSchemaModelType } from "src/components/audiences/utils";
import { SchemaModelType } from "src/types/schema";
import { WhereEachText } from "./related-filter-shared";
import { FieldError } from "src/components/field-error";
import { useNestedFilteringFlag } from "src/components/explore/utils/computed-flag";

const relatedTo = {
  operator: NumberOperator.GreaterThanOrEqualTo,
  value: 1,
};

const notRelatedTo = {
  operator: NumberOperator.Equals,
  value: 0,
};

const getRelatedOptions = (plural: boolean) => [
  { label: plural ? "Have" : "Has", value: "related" },
  { label: `${plural ? "Do" : "Does"} not have`, value: "not related" },
];

const getSemanticTypeRelatedOptions = (
  plural: boolean,
  isContainerSemanticType: boolean,
) => {
  if (isContainerSemanticType) {
    return [
      { label: "Has", value: "related" },
      {
        label: "Does not have",
        value: "not related",
      },
    ];
  }
  return [
    { label: `${plural ? "Are" : "Is"} a member`, value: "related" },
    { label: `${plural ? "Are" : "Is"} not a member`, value: "not related" },
  ];
};

// biome-ignore lint/style/useComponentExportOnlyModules: only imported for tests.
export const shouldShowSemanticLabelUI = ({
  appHouseholdingEnabled,
  relationship,
  effectiveSemanticType,
  relatedSemanticType,
}: {
  appHouseholdingEnabled: boolean;
  relationship:
    | {
        to_model: Pick<
          RelationshipWithoutFilterableColumnsFragment["to_model"],
          "is_schema" | "primary_key"
        >;
      }
    | undefined;
  effectiveSemanticType: string | null | undefined;
  relatedSemanticType: string | null | undefined;
}) => {
  return (
    appHouseholdingEnabled &&
    !!relationship?.to_model.is_schema &&
    // Check for a relationship we can correctly label
    effectiveSemanticType === ModelSemanticType.People &&
    (relatedSemanticType === ModelSemanticType.Households ||
      relatedSemanticType === ModelSemanticType.Accounts)
  );
};

const missingRelatedModelWarningMsg =
  "The related model may have been updated or deleted";

const getTopLevelSubcondition = (condition: NumberOfCondition) => {
  return condition.subconditions?.[0];
};

export const NumberOfFilter: FC<Readonly<FilterProps<NumberOfCondition>>> = (
  props,
) => {
  const { appHouseholdingEnabled } = useFlags();
  const queryBuilderNestedFiltering = useNestedFilteringFlag();
  const {
    condition: unformattedCondition,
    propertyComparisonType,
    onUpdate,
    level = 0,
  } = props;

  const { parentModelSemanticType, enableNestedFiltering, enableRealtime } =
    useQueryBuilderContext();
  const {
    relationships,
    nestingLevel,
    modelName,
    parentFilterOptionType,
    semanticType: filterModelSemanticType,
  } = useFilterableOptionsContext();
  const isNestedFilteringEnabled =
    enableNestedFiltering && queryBuilderNestedFiltering;

  // Wrap subconditions in one and/or condition
  const condition = formatSubconditions(unformattedCondition);
  const topLevelSubcondition = getTopLevelSubcondition(condition);
  // Assumption is that event condition will be formatted to always have one And/Or subcondition as a child (if there are subconditions)
  const subconditions = isAndOrCondition(topLevelSubcondition)
    ? topLevelSubcondition.conditions
    : [];

  const filterId = useMemo<string>(uuidv4, []);

  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();

  const filterErrors = getErrors(filterId, { allErrors: true });
  const modelError = filterErrors?.relationshipId;
  const valueError = filterErrors?.value;
  const conditionError = filterErrors?.condition;

  const isNested = nestingLevel > 0;

  // Realtime audiences don't support nested conditions
  const unsupportedCondition = isNested && enableRealtime;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(
      filterId,
      validateNumberOfCondition(condition, {
        isRealtimeAudience: unsupportedCondition,
      }),
    );

    return () => {
      removeErrors([filterId]);
    };
  }, [
    condition.relationshipId,
    condition.value,
    filterId,
    unsupportedCondition,
  ]);

  const relationship = relationships?.find(
    ({ id }) => id?.toString() === condition.relationshipId?.toString(),
  );
  const relatedModel = relationship?.to_model;

  const relatedSemanticType = relationship?.to_model.semantic_type;
  const effectiveSemanticType =
    filterModelSemanticType ?? parentModelSemanticType;
  const isContainerSemanticType =
    effectiveSemanticType === ModelSemanticType.Households ||
    effectiveSemanticType === ModelSemanticType.Accounts;
  const showSemanticLabelUI = shouldShowSemanticLabelUI({
    appHouseholdingEnabled,
    relationship,
    effectiveSemanticType,
    relatedSemanticType,
  });

  // If the underlying related model is removed/updated, the relationId will still point to the old, invalid model.
  const invalidRelationshipState =
    condition.relationshipId != null && relatedModel?.id == null;

  const isNotRelated =
    condition.value === 0 && condition.operator === NumberOperator.Equals;
  const relatedValue = isNotRelated ? "not related" : "related";

  const relatedOptions = useMemo(
    () => getRelatedOptions(!isNested),
    [isNested],
  );
  const semanticTypeRelatedOptions = useMemo(
    () => getSemanticTypeRelatedOptions(!isNested, isContainerSemanticType),
    [isNested, isContainerSemanticType],
  );
  const relatedLabel = relatedOptions?.find(
    ({ value }) => relatedValue === value,
  )?.label;
  const operatorLabel = numberOfOperatorOptions.find(
    ({ value }) => value === condition.operator,
  )?.label;

  const updateWrappingConditionType = (
    type: ConditionType.And | ConditionType.Or,
  ) => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    onUpdate((condition) => {
      condition.subconditions = [
        {
          type: type,
          id: topLevelSubcondition.id,
          conditions: topLevelSubcondition?.conditions ?? [],
        },
      ];
    });
  };

  const addSubcondition = (newCondition: NestableCondition) => {
    if (!isAndOrCondition(topLevelSubcondition)) {
      return;
    }

    const newSubconditionsList = [
      {
        type: topLevelSubcondition.type,
        id: topLevelSubcondition.id,
        conditions: [...topLevelSubcondition.conditions, newCondition],
      },
    ];

    onUpdate((condition) => {
      condition.subconditions = newSubconditionsList;
    });
  };

  const generateSubconditionUpdaters = useMemo(
    () => createSubconditionUpdater(onUpdate, { disableSorting: true }),
    [onUpdate],
  );

  const removeSubcondition = (id?: string) => {
    onUpdate((condition) => removeSubconditionMutative(condition, id));
  };

  const makeOnGroupSubcondition = (index: number) => () => {
    onUpdate((draft) => {
      const top = getTopLevelSubcondition(draft);
      if (isAndOrCondition(top)) {
        groupSubcondition(index, top);
      }
    });
  };

  const makeOnUngroupSubcondition = (index: number) => () => {
    onUpdate((draft) => {
      const top = getTopLevelSubcondition(draft);
      if (isAndOrCondition(top)) {
        ungroupSubcondition(index, top);
      }
    });
  };

  return (
    <>
      <HStack gap={2}>
        {isNested && (
          <WhereEachText
            modelName={modelName}
            filterOptionType={parentFilterOptionType}
            semanticType={
              showSemanticLabelUI ? effectiveSemanticType : undefined
            }
          />
        )}
        {showSemanticLabelUI ? (
          <Select
            removePortal
            options={semanticTypeRelatedOptions}
            placeholder="Select relation"
            value={relatedValue}
            width="auto"
            onChange={(value) => {
              if (value === "related") {
                onUpdate((condition) => {
                  condition.value = relatedTo.value;
                  condition.operator = relatedTo.operator;
                });
              } else {
                onUpdate((condition) => {
                  condition.value = notRelatedTo.value;
                  condition.operator = notRelatedTo.operator;
                });
              }
            }}
          />
        ) : (
          <Filter
            isEmpty={false} // Field is automatically populated
            content={
              <Row align="flex-start" gap={2}>
                <Select
                  removePortal
                  options={relatedOptions}
                  placeholder="Select relation"
                  value={relatedValue}
                  width="auto"
                  onChange={(value) => {
                    if (value === "related") {
                      onUpdate((condition) => {
                        condition.value = relatedTo.value;
                        condition.operator = relatedTo.operator;
                      });
                    } else {
                      onUpdate((condition) => {
                        condition.value = notRelatedTo.value;
                        condition.operator = notRelatedTo.operator;
                      });
                    }
                  }}
                />
                {isNotRelated ? null : (
                  <>
                    <Select
                      removePortal
                      options={numberOfOperatorOptions}
                      placeholder="Filter on"
                      value={condition.operator}
                      width="auto"
                      onChange={(operator) => {
                        onUpdate((condition) => {
                          if (operator) {
                            condition.operator = operator;
                          }
                        });
                      }}
                    />
                    <Column>
                      <HiddenFormLabel label="Filter on amount">
                        <NumberInput
                          isInvalid={Boolean(valueError)}
                          placeholder="amount..."
                          value={condition.value}
                          width="auto"
                          onChange={(value) => {
                            onUpdate((condition) => {
                              if (value != null) {
                                condition.value = value;
                              }
                            });
                          }}
                        />
                      </HiddenFormLabel>
                      {valueError && <ErrorMessage>{valueError}</ErrorMessage>}
                    </Column>
                  </>
                )}
              </Row>
            }
            error={valueError}
          >
            <Text fontWeight="medium">
              <Text color="text.secondary" fontWeight="normal">
                {relatedLabel}
              </Text>
              {!isNotRelated &&
                ` ${operatorLabel} ${
                  !isPresent(condition.value) ? "___" : condition.value
                }`}
            </Text>
          </Filter>
        )}
        <Text color="text.secondary">
          {showSemanticLabelUI && !!relatedSemanticType
            ? isContainerSemanticType
              ? `${relatedSemanticType} in`
              : `of ${relatedSemanticType === ModelSemanticType.Households ? "a" : "an"} ${singular(relatedSemanticType)} in`
            : "record in"}
        </Text>
        <FilterPopover
          {...props}
          onChange={(newCondition) => onUpdate(() => newCondition)}
          condition={condition}
          hasError={Boolean(modelError) || invalidRelationshipState}
        />
        {invalidRelationshipState && (
          <Tooltip message={missingRelatedModelWarningMsg}>
            <Box
              as={WarningIcon}
              boxSize={6}
              color="warning.400"
              aria-label={missingRelatedModelWarningMsg}
            />
          </Tooltip>
        )}
      </HStack>
      {relatedModel && (
        <Column pl={8} gap={4} mt={2}>
          <FilterableOptionsProvider
            modelId={relatedModel.id}
            sections={getRelationSubconditionFilterableSections({
              isParentModel:
                getSchemaModelType(relatedModel) === SchemaModelType.Parent,
              enableNestedFiltering: isNestedFilteringEnabled,
              isRealtimeAudience: enableRealtime,
            })}
            parentFilterOptionType={FilterOption.Relation}
            semanticType={relationship?.to_model.semantic_type}
          >
            {subconditions.map((subcondition, index) => {
              return (
                <RelatedModelSubcondition
                  key={subcondition.id ?? index}
                  isFirstCondition={index === 0}
                  subcondition={subcondition}
                  audience={props.audience}
                  parent={relatedModel as unknown as MinimalParentModel} // TODO(nolan): can we fix this plz
                  propertyComparisonType={propertyComparisonType}
                  generateOnUpdate={generateSubconditionUpdaters}
                  onRemove={() => removeSubcondition(subcondition.id)}
                  isWrappedWithAndCondition={
                    topLevelSubcondition?.type === ConditionType.And
                  }
                  onToggleWrappingConditionType={() =>
                    updateWrappingConditionType(
                      topLevelSubcondition?.type === ConditionType.And
                        ? ConditionType.Or
                        : ConditionType.And,
                    )
                  }
                  onGroup={makeOnGroupSubcondition(index)}
                  onUngroup={makeOnUngroupSubcondition(index)}
                  numberOfFilterLevel={level}
                />
              );
            })}
            <Row>
              <WhereButton
                isDisabled={!condition.relationshipId}
                // TODO(nolan) fix types
                parent={relatedModel as unknown as MinimalParentModel}
                // Is there a way to constrain filter popover's return types to the context?
                onChange={addSubcondition as (newCondition: Condition) => void}
              />
            </Row>
          </FilterableOptionsProvider>
        </Column>
      )}

      {conditionError && <FieldError error={conditionError} />}
    </>
  );
};

interface RelatedModelSubconditionProps {
  subcondition: NestableCondition;
  audience?: Audience;
  parent: MinimalParentModel;
  generateOnUpdate: (
    id?: string,
  ) => (
    updater: UpdaterAdvanced<Condition | FunnelCondition, NestableCondition>,
  ) => void;
  onRemove: () => void;
  isWrappedWithAndCondition: boolean;
  onToggleWrappingConditionType: () => void;
  isFirstCondition: boolean;
  propertyComparisonType?: PropertyComparisonType;
  onGroup: () => void;
  onUngroup: () => void;
  numberOfFilterLevel: number;

  // Householding
  semanticType?: string;
}

const RelatedModelSubcondition = (props: RelatedModelSubconditionProps) => {
  const {
    subcondition,
    generateOnUpdate,
    onRemove,
    audience,
    parent,
    isWrappedWithAndCondition,
    onToggleWrappingConditionType,
    isFirstCondition,
    propertyComparisonType,
    semanticType,
    onGroup,
    onUngroup,
    numberOfFilterLevel,
  } = props;
  const { appHouseholdingEnabled } = useFlags();
  const queryBuilderNestedFiltering = useNestedFilteringFlag();
  const { enableNestedFiltering } = useQueryBuilderContext();
  const isNestedFilteringEnabled =
    queryBuilderNestedFiltering && enableNestedFiltering;

  const onUpdate = useMemo(
    () => generateOnUpdate(subcondition.id),
    [generateOnUpdate, subcondition.id],
  );

  if (subcondition.type !== ConditionType.Property) {
    if (!isNestedFilteringEnabled) {
      return null;
    }
    return (
      <NestedRelationConditionWrapper
        onRemove={onRemove}
        isFirstCondition={isFirstCondition}
        onToggleWrappingConditionType={onToggleWrappingConditionType}
        isWrappedWithAndCondition={isWrappedWithAndCondition}
      >
        <ConditionField
          condition={subcondition}
          parent={parent}
          audience={audience}
          onUpdate={onUpdate}
          onRemove={onRemove}
          onUngroup={onUngroup}
          level={numberOfFilterLevel + 1}
        />
      </NestedRelationConditionWrapper>
    );
  }

  // Householding traits may only be selected if both the parent model and this model are 'parent models'.
  // The parent model must be labeled as 'people' and the related model must be labeled as 'households' or 'accounts'.
  if (
    appHouseholdingEnabled &&
    isTraitCondition(subcondition) &&
    semanticType
  ) {
    return (
      <NestedTraitFilter
        {...props}
        columns={undefined}
        condition={subcondition}
        semanticType={semanticType}
        modelId={parent?.id ?? null}
        includeParentInPropertyComparison={false}
        propertyComparisonType={propertyComparisonType}
        isWrappedWithAndCondition={isWrappedWithAndCondition}
        isFirstCondition={isFirstCondition}
        audience={audience}
        onUpdate={onUpdate as OnUpdate<TraitCondition | InlineTraitCondition>}
        onRemove={onRemove}
        onToggleWrappingConditionType={onToggleWrappingConditionType}
        containerSx={{
          alignItems: "flex-start",
          width: "100%",
        }}
      />
    );
  }

  return (
    <NestedPropertyFilter
      {...props}
      lhsPropertyFilterVariant="dropdown"
      columns={undefined}
      condition={subcondition}
      isFirstCondition={isFirstCondition}
      propertyComparisonType={propertyComparisonType}
      includeParentInPropertyComparison={false}
      isWrappedWithAndCondition={isWrappedWithAndCondition}
      parent={parent}
      audience={audience}
      onUpdate={onUpdate as OnUpdate<PropertyCondition>}
      onRemove={onRemove}
      onToggleWrappingConditionType={onToggleWrappingConditionType}
      containerSx={{ alignItems: "flex-start", width: "100%" }}
      onGroup={onGroup}
      level={numberOfFilterLevel}
    />
  );
};
